import {
  MapPin, Filter, Bot, Sparkles, MessageCircle, Search, Settings2,
  Send, Globe, Info, Plug, Webhook, Trello, Activity,
  ArrowRight, Thermometer,
} from "lucide-react";
import { Link } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Logo from "../components/Logo";
import LangToggle from "../components/LangToggle";
import ThemeToggle from "../components/ThemeToggle";

// Each step now points to a NATIVE AgenteLead route + native CTA, no external scrapers.
const SECTIONS = [
  {
    id: "search-filters",
    icon: Filter,
    color: "#3B82F6",
    title: "1. Pesquisa Direcionada por Nicho",
    body:
      "Em vez de copiar-colar do Google Maps manualmente, use a busca nativa de Prospecção do AgenteLead Pro. Combine palavras-chave de nicho com cidade — ex.: \"empresas de construção em Orlando\" ou \"clínicas de estética em Miami\" — e a plataforma já trás os resultados com Score Digital calculado, deduplicação automática e badge QUENTE/MORNO/FRIO.",
    steps: [
      "Vá para Prospecção e selecione o nicho (Roofing, HVAC, Plumbing, Construction…)",
      "Digite a cidade no campo de localização (ex.: \"Orlando, FL 32801\") e ajuste o raio",
      "Use o filtro de status para ver só negócios que estão abertos no momento",
      "Cada lead já vem com nome, telefone, site, endereço, rating, reviews e Score automaticamente",
    ],
    cta: { label: "Abrir Prospecção", to: "/" },
  },
  {
    id: "automation",
    icon: Bot,
    color: "#8B5CF6",
    title: "2. Automação em Escala — Sem Extensão Externa",
    body:
      "Para varrer várias cidades de uma vez, use o Bulk Search nativo do AgenteLead Pro. Você passa uma lista de cidades, define o nicho e a plataforma roda buscas paralelas, deduplica por place_id e (opcionalmente) salva automaticamente todos os QUENTE no seu CRM Kanban. Sem CSV, sem extensão, sem importar/exportar.",
    steps: [
      "Em Prospecção, abra o modal Bulk Search e cole as cidades separadas por vírgula",
      "Ative \"Auto-save QUENTE\" para que leads de alta prioridade entrem direto no Kanban",
      "Ao terminar, abra a aba CRM Kanban — todos já estão na coluna \"Novo\" prontos para você atacar",
      "Filtros nativos no Kanban (busca, temperatura, nicho) e drag & drop para mover entre etapas",
    ],
    cta: { label: "Ir para CRM Kanban", to: "/kanban" },
  },
  {
    id: "enrichment",
    icon: Sparkles,
    color: "#F59E0B",
    title: "3. Enriquecimento Automático",
    body:
      "Após salvar um lead, o AgenteLead Pro inicia em background um job de enriquecimento que faz scraping do site para detectar LinkedIn, Facebook/Instagram, blog/SEO ativo e SSL — sinais que entram no Score Digital. O algoritmo classifica QUENTE, MORNO ou FRIO. Para integrações de e-mail/decisor (Hunter, Apollo, etc.), use a aba Integrações e nossos Webhooks — o AgenteLead dispara eventos `save`, `bulk_search`, `enrich` em tempo real para qualquer destino HTTP.",
    steps: [
      "Salve qualquer lead → enrichment_scheduled = true automaticamente",
      "O job atualiza social signals + recalcula score sem você fazer nada",
      "Quer reprocessar? Abra o card e clique em \"Re-enriquecer\" no detalhe do lead",
      "Plug Hunter / Apollo / Pipedrive via Integrações nativas ou Webhooks → seu fluxo continua dentro da plataforma",
    ],
    cta: { label: "Ver Integrações nativas", to: "/integrations" },
    note: {
      icon: Info,
      title: "Como ler o Score",
      body:
        "Atenção à lógica invertida: quanto MENOR o score, mais QUENTE o lead — porque score baixo significa baixa maturidade digital, ou seja, MAIOR oportunidade de venda para você. QUENTE: 0-33 · MORNO: 34-66 · FRIO: 67-100.",
    },
  },
  {
    id: "approach",
    icon: MessageCircle,
    color: "#22C55E",
    title: "4. Abordagem Personalizada com IA",
    body:
      "Com o lead enriquecido, deixe a IA fazer o trabalho de personalização. O AgenteLead Pro usa GPT para gerar uma Auditoria em 6 seções (Web Presence, Local SEO, Social, Reviews, Oportunidades e Proposta de Valor) e um e-mail de outreach que cita o rating Google, comentários reais e detalhes específicos do site — abordagens hiper-personalizadas costumam render 2–3× mais respostas do que cold mail genérico.",
    steps: [
      "No card do lead, clique em \"Run Audit\" — análise GPT em 6 seções é gerada na hora",
      "Em seguida \"Send Email\" — IA escreve assunto e corpo, você ajusta o tom e dispara",
      "Use \"Send WhatsApp\" no Kanban para sequência multicanal (Twilio Sandbox/Production)",
      "Mova o card no Kanban (Contactado → Qualificado → Proposta) — atividade fica no histórico",
    ],
    cta: { label: "Rodar primeira auditoria", to: "/" },
  },
];

// Replaced the old grid of external paid tools with a curated grid of NATIVE AgenteLead features.
const NATIVE_FEATURES = [
  { name: "CRM Kanban", desc: "Drag & drop, follow-up, histórico", to: "/kanban", icon: Trello, color: "#3B82F6" },
  { name: "Integrações", desc: "Conecte Pipedrive, Mailchimp, Apollo…", to: "/integrations", icon: Plug, color: "#8B5CF6" },
  { name: "Webhooks", desc: "Eventos em tempo real para Zapier/N8N/Make", to: "/webhooks", icon: Webhook, color: "#F59E0B" },
  { name: "Activity Log", desc: "Auditoria completa de todas as ações", to: "/log", icon: Activity, color: "#22C55E" },
  { name: "Dashboard", desc: "Funil de conversão + KPIs clicáveis", to: "/dashboard", icon: Settings2, color: "#EC4899" },
];

export default function GoogleMapsPlaybook() {
  return (
    <div className="min-h-screen bg-[var(--al-bg)] text-[var(--al-text)]">
      <Sidebar />
      <div className="ml-[var(--al-sidebar-w,260px)]">
        <header
          data-testid="playbook-header"
          className="sticky top-0 z-30 bg-[var(--al-card-bg)] border-b border-[var(--al-border)] px-8 py-4 flex items-center justify-between gap-4"
        >
          <div className="flex items-center gap-4 min-w-0">
            <Logo className="w-9 h-9 object-contain" />
            <div className="min-w-0">
              <h1 className="text-[16px] font-semibold text-[var(--al-text)] truncate">
                <MapPin className="inline-block w-4 h-4 mr-1.5 -mt-0.5 text-[#3B82F6]" />
                Google Maps Playbook — Captação B2B
              </h1>
              <p className="text-[12.5px] text-[var(--al-text-muted)] mt-0.5">
                Pesquisa · Automação · Enriquecimento · Abordagem — tudo nativo no AgenteLead Pro
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <ThemeToggle />
            <LangToggle />
          </div>
        </header>

        <main className="px-8 py-6 max-w-[1100px] mx-auto space-y-6">
          {/* Hero / intro */}
          <section
            data-testid="playbook-hero"
            className="al-card p-6 bg-gradient-to-br from-[#EFF6FF] to-white dark:from-[#0F172A] dark:to-[#18181B] border-[#DBEAFE] dark:border-[#1E293B]"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#3B82F6] text-white flex items-center justify-center shrink-0">
                <MapPin className="w-6 h-6" />
              </div>
              <div className="min-w-0">
                <h2 className="text-[18px] font-bold text-[var(--al-text)]">
                  O Google Maps é uma mina de leads B2B locais — e o AgenteLead Pro extrai tudo por você
                </h2>
                <p className="text-[13.5px] text-[var(--al-text-muted)] mt-2 leading-relaxed">
                  Em vez de pular entre 5 ferramentas pagas (extensão, scraper, enriquecimento, CRM, e-mail),
                  este playbook mostra como capturar, qualificar, enriquecer e abordar leads inteiramente dentro
                  da plataforma. Cada etapa abaixo abre direto a tela onde você executa.
                </p>
                <div className="flex items-center gap-2 mt-4 flex-wrap">
                  <Link
                    data-testid="playbook-cta-prospecting"
                    to="/"
                    className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg al-btn-primary text-[12.5px] font-semibold"
                  >
                    <Search className="w-4 h-4" /> Começar a Prospectar
                  </Link>
                  <a
                    href="https://www.google.com/maps"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="playbook-open-maps"
                    className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg border border-[var(--al-border)] bg-[var(--al-card-bg)] text-[12.5px] font-medium hover:border-[#3B82F6] hover:text-[#3B82F6] transition-colors"
                  >
                    <Globe className="w-4 h-4" /> Abrir Google Maps
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* Sections */}
          <div className="space-y-4">
            {SECTIONS.map((sec) => {
              const Icon = sec.icon;
              return (
                <section
                  key={sec.id}
                  data-testid={`playbook-section-${sec.id}`}
                  className="al-card p-6"
                >
                  <div className="flex items-start gap-4">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                      style={{ background: `${sec.color}1A`, color: sec.color }}
                    >
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-[16px] font-bold text-[var(--al-text)]">{sec.title}</h3>
                      <p className="text-[13.5px] text-[var(--al-text-muted)] mt-2 leading-relaxed">
                        {sec.body}
                      </p>

                      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2.5">
                        {sec.steps.map((s, i) => (
                          <div key={s} className="flex items-start gap-2.5">
                            <span
                              className="w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5"
                              style={{ background: `${sec.color}1A`, color: sec.color }}
                            >
                              {i + 1}
                            </span>
                            <span className="text-[12.5px] text-[var(--al-text)] leading-snug">{s}</span>
                          </div>
                        ))}
                      </div>

                      {sec.note && (
                        <div
                          data-testid={`playbook-note-${sec.id}`}
                          className="mt-4 p-3 rounded-lg border border-[#FDE68A] bg-[#FFFBEB] dark:bg-[#451A03]/40 dark:border-[#92400E] flex items-start gap-2.5"
                        >
                          <Thermometer className="w-4 h-4 text-[#D97706] dark:text-[#FBBF24] shrink-0 mt-0.5" />
                          <div>
                            <div className="text-[12px] font-bold text-[#92400E] dark:text-[#FBBF24]">
                              {sec.note.title}
                            </div>
                            <div className="text-[12px] text-[#78350F] dark:text-[#FCD34D] leading-relaxed mt-0.5">
                              {sec.note.body}
                            </div>
                          </div>
                        </div>
                      )}

                      {sec.cta && (
                        <div className="mt-4">
                          <Link
                            data-testid={`playbook-cta-${sec.id}`}
                            to={sec.cta.to}
                            className="inline-flex items-center gap-1.5 px-3 h-8 rounded-lg text-[12.5px] font-semibold transition-colors"
                            style={{
                              background: `${sec.color}15`,
                              color: sec.color,
                              border: `1px solid ${sec.color}50`,
                            }}
                          >
                            {sec.cta.label} <ArrowRight className="w-3.5 h-3.5" />
                          </Link>
                        </div>
                      )}
                    </div>
                  </div>
                </section>
              );
            })}
          </div>

          {/* Native features grid (replaces former external tools list) */}
          <section data-testid="playbook-native-features" className="al-card p-6">
            <div className="flex items-center gap-2 mb-1">
              <Settings2 className="w-4 h-4 text-[#3B82F6]" />
              <h3 className="text-[14px] font-bold text-[var(--al-text)]">Continue dentro do AgenteLead Pro</h3>
            </div>
            <p className="text-[12px] text-[var(--al-text-muted)] mb-4">
              Recursos nativos que estendem este playbook — sem precisar de ferramentas externas pagas.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
              {NATIVE_FEATURES.map((f) => {
                const Icon = f.icon;
                return (
                  <Link
                    key={f.to + f.name}
                    to={f.to}
                    data-testid={`playbook-native-${f.name.toLowerCase().replace(/\s+/g, "-")}`}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-[var(--al-border)] hover:border-[#3B82F6] hover:bg-[#EFF6FF]/30 dark:hover:bg-[#1E293B]/30 transition-all group"
                  >
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                      style={{ background: `${f.color}1A`, color: f.color }}
                    >
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-semibold text-[var(--al-text)] truncate">{f.name}</div>
                      <div className="text-[11px] text-[var(--al-text-muted)] truncate">{f.desc}</div>
                    </div>
                    <ArrowRight className="w-3.5 h-3.5 text-[#9CA3AF] group-hover:text-[#3B82F6] shrink-0" />
                  </Link>
                );
              })}
            </div>
          </section>

          {/* CTA back to Prospecting */}
          <section className="al-card p-6 flex items-center justify-between flex-wrap gap-4 bg-[#1A1A2E] text-white border-[#1A1A2E]">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                <Search className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="font-bold text-[14px]">Pronto para executar?</div>
                <div className="text-[12px] text-white/70">
                  Abra a Prospecção, escolha o nicho e a cidade — em minutos você terá leads QUENTE com Auditoria + Email pronta.
                </div>
              </div>
            </div>
            <Link
              to="/"
              data-testid="playbook-final-cta"
              className="inline-flex items-center gap-1.5 px-4 h-10 rounded-lg al-btn-primary text-[13px] font-semibold"
            >
              <Send className="w-4 h-4" /> Ir para Prospecção
            </Link>
          </section>
        </main>
      </div>
    </div>
  );
}
